import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, X, Loader2, CheckCircle, XCircle, Info, AlertCircle, Clock, Eye, Edit2, Save, Check, ArrowLeft, RefreshCw } from 'lucide-react';
import { apiClient } from '../../lib/api/apiClient';
import HROnboardingPanel from '../HROnboardingPanel';
import AllEforms from '../AllEforms';
import EmployeeUpdateRequest from './EmployeeUpdateRequest';
import { useEmployeeStream } from '../../hooks/EmployeStream';
import UserManagement from '../../components/UserManagement';
import { useAuth } from '../../context/AuthContext';
import PDFMergeUploader from '../../components/PDFMergeUploader';

const MainOnboardingSystem = () => {
  const [currentView, setCurrentView] = useState('new'); // 'new' or 'review'
  const {user} = useAuth();
  
  // Upload component state - Fixed: Store as object to match API response
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [tasksInProgress, setTasksInProgress] = useState({}); // Changed to object
  const [taskMetadata, setTaskMetadata] = useState({}); // Store additional file info
  const fileInputRef = useRef(null);
  
  // Review component state
  const [employees, setEmployees] = useState([]);
  const [currentEmployeeIndex, setCurrentEmployeeIndex] = useState(0);
  const [isEditing, setIsEditing] = useState({});
  const [editedData, setEditedData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Common state
  const [toasts, setToasts] = useState([]);

  // Polling for task status updates
  useEffect(() => {
    if (Object.keys(tasksInProgress).length > 0) {
      const interval = setInterval(checkTasksStatus, 1000*10);
      return () => clearInterval(interval);
    }
  }, [tasksInProgress]);

  // Load tasks in progress on mount
  useEffect(() => {
    fetchTasksInProgress();
  }, []);

  const currentEmployee = employees[currentEmployeeIndex] || null;

  // Toast management
  const showToast = (message, type = 'info') => {
    const id = Date.now();
    const toast = { id, message, type };
    setToasts(prev => [...prev, toast]);
    
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // API calls - Fixed to handle new response structure
  const fetchTasksInProgress = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/tasks-in-progress`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle the new API response structure
        setTasksInProgress(data.tasks || {});
        
        // Log for debugging
        console.log('Fetched tasks in progress:', data);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const uploadDocument = async (file) => {
    try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/upload-document`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Upload failed");
    }

    return response.json();
    } catch (error) {
      console.error("Error uploading document:", error);
      showToast("Error uploading document", "error");
    }
  };
  const [progress, setProgress] = useState(10);
  const checkTaskStatus = async (taskId) => {
    const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/task-status/${taskId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      },
    });
    const data = await response.json();

    if (response.ok) {
      setProgress(prev=>prev<99?prev+1:99);
      return {status: data.status, progress: progress};
    }
    throw new Error("Failed to fetch task status");
  };

  const fetchProcessedEmployees = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/processed-documents`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      
      if (response.ok) {
        const {count, processed_data} = await response.json();
        const processedData = processed_data.map((emp) => {
          return {
            id: emp.eid,
            status: "pending_review",
            filename: emp.name,
            processed_at: emp.processed_at,
            data: {...emp},
          };
        });
        console.log(processedData);
        setEmployees(processedData || []);
        if (processedData && processedData.length > 0) {
          showToast(`Loaded ${processedData.length} processed employee(s)`, 'success');
        }
      } else {
        showToast('Failed to load employee data', 'error');
        loadMockData();
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      showToast('Error loading employee data', 'error');
      loadMockData();
    } finally {
      setIsLoading(false);
    }
  };

  const loadMockData = () => {
    const mockEmployees = [
        {
            id: "emp_001",
            filename: "Hukum chand",
            processed_at: new Date().toISOString(),
            status: "pending_review",
            data: {
              "eid": "00600168",
              "name": "",
              "personal_information": {
                  "employee_name": "",
                  "father_name": "Hukam Chand",
                  "mother_name": "Govind Ram",
                  "date_of_birth": "1979-01-01 00:00:00",
                  "place_of_birth": "",
                  "gender": "",
                  "nationality": "",
                  "religion": "",
                  "marital_status": "unmarried",
                  "blood_group": "",
                  "languages_known": []
              },
              "address_information": {
                  "permanent_address": {
                      "house": "",
                      "street": "",
                      "landmark": "",
                      "locality": "",
                      "city_or_village": "",
                      "post_office": "",
                      "sub_district": "",
                      "district": "",
                      "state": "",
                      "pincode": ""
                  },
                  "current_address": {
                      "house": "",
                      "street": "",
                      "landmark": "",
                      "locality": "",
                      "city_or_village": "",
                      "post_office": "",
                      "sub_district": "",
                      "district": "",
                      "state": "",
                      "pincode": ""
                  }
              },
              "contact_information": {
                  "phone_number": "",
                  "email": "",
                  "emergency_contact": {
                      "name": "",
                      "mobile": "",
                      "email": ""
                  }
              },
              "identification_documents": {
                  "aadhaar_number": "",
                  "pan_number": "AKRPC0251A"
              },
              "employment_details": {
                  "designation": "",
                  "grade": "",
                  "eid": "",
                  "uan_number": "",
                  "date_of_joining": null,
                  "date_of_appointment": null,
                  "location": "",
                  "division": "",
                  "reporting_manager": "",
                  "source_of_candidate": "Other",
                  "department": "",
                  "last_organisation": "",
                  "total_experience": null,
                  "last_promotion_date": null,
                  "official_email": ""
              },
              "salary_and_compensation": {
                  "at_joining": {
                      "ctc": null,
                      "basic": null,
                      "gross": null,
                      "incentive": null
                  },
                  "yearly_compensation": {
                      "2022-23": {
                          "ctc": "",
                          "basic": "",
                          "gross": "",
                          "incentive": ""
                      },
                      "2023-24": {
                          "ctc": "",
                          "basic": "",
                          "gross": "",
                          "incentive": ""
                      },
                      "2024-25": {
                          "ctc": "",
                          "basic": "",
                          "gross": "",
                          "incentive": ""
                      },
                      "2025-26": {
                          "ctc": "",
                          "basic": "",
                          "gross": "",
                          "incentive": ""
                      }
                  }
              },
              "bank_details": {
                  "bank_name": "",
                  "account_number": "",
                  "ifsc_code": ""
              },
              "nominee_information": [
                  {
                      "nominee_name": "",
                      "relationship": "",
                      "nominee_age": null,
                      "nominee_dob": null,
                      "share_percentage": null
                  }
              ],
              "declarations_and_compliance": {
                  "physical_disabilities": "",
                  "major_illness": "",
                  "court_cases": "",
                  "police_custody": ""
              },
              "education": {
                  "tenth": {
                      "completed": "",
                      "education_source": ""
                  },
                  "twelfth": {
                      "completed": "",
                      "education_source": ""
                  },
                  "graduation": {
                      "completed": "",
                      "degree": "",
                      "education_source": ""
                  },
                  "post_graduation": {
                      "completed": "",
                      "degree": "",
                      "education_source": ""
                  },
                  "professional": {
                      "completed": "",
                      "degree": "",
                      "education_source": ""
                  },
                  "other": []
              }
          }
          }
    ];
    setEmployees(mockEmployees);
  };

  // Fixed: Handle object structure for tasks
  const checkTasksStatus = async () => {
    const taskIds = Object.keys(tasksInProgress);
    const updatedTasks = { ...tasksInProgress };
    
    for (const taskId of taskIds) {
      try {
        const status = await checkTaskStatus(taskId);
        
        if (status.status === 'completed' || status.status === 'failed') {
          if (status.status === 'completed') {
            const metadata = taskMetadata[taskId];
            showToast(`Document "${metadata?.filename || 'Unknown'}" processed successfully!`, 'success');
          } else {
            const metadata = taskMetadata[taskId];
            showToast(`Processing failed for "${metadata?.filename || 'Unknown'}"`, 'error');
          }
          // Remove completed/failed tasks from progress tracking
          delete updatedTasks[taskId];
        } else {
          updatedTasks[taskId] = { ...updatedTasks[taskId], ...status };
        }
      } catch (error) {
        console.error('Error checking task status:', error);
      }
    }
    
    setTasksInProgress(updatedTasks);
  };
  const [employeeId, setEmployeeId] = useState('');
  function addDoubleZero(value) {
    return "00" + value.replace(/^0+/, "");
  }
  // Upload handlers - Fixed to use object structure
  const handleFileUpload = async (fileblob) => {
    if (!employeeId) {
      showToast('Please enter employee ID', 'error');
      return;
    }
    const files = [fileblob];
    console.log(files);
    console.log(fileblob);
    if (files.length === 0) return;
  
    const invalidFiles = files.filter(file => !file.type.includes('pdf'));
    if (invalidFiles.length > 0) {
      showToast('Please select only PDF files', 'error');
      return;
    }
  
    const oversizedFiles = files.filter(file => file.size > 20 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      showToast('File size should not exceed 20MB', 'error');
      return;
    }
  
    setIsUploading(true);
  
    for (const file of files) {
      try {
        // Create a new file with Employee ID prefix
        const newFileName = `${addDoubleZero(employeeId)}_${file.name}`;
        const renamedFile = new File([file], newFileName, { type: file.type });
  
        showToast(`Uploading ${renamedFile.name}...`, 'info');
        
        const result = await uploadDocument(renamedFile);
        
        setEmployeeId('');
        
        // Store task in progress with object structure
        setTasksInProgress(prev => ({
          ...prev,
          [result.task_id]: {
            status: 'processing',
            progress: 0
          }
        }));
        
        // Store metadata separately
        setTaskMetadata(prev => ({
          ...prev,
          [result.task_id]: {
            filename: renamedFile.name,
            uploaded_at: new Date().toISOString(),
            file_size: renamedFile.size
          }
        }));
        
        setUploadedFiles(prev => [...prev, renamedFile]);
        
        showToast(`${renamedFile.name} uploaded successfully. Processing in background...`, 'success');
        
      } catch (error) {
        showToast(`Failed to upload ${file.name}`, 'error');
      }
    }
  
    setIsUploading(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  

  // Review handlers
  const startEditing = (section, field) => {
    const key = `${section}.${field}`;
    setIsEditing(prev => ({ ...prev, [key]: true }));
    
    const value = field.includes('.') 
      ? field.split('.').reduce((obj, key) => obj?.[key], currentEmployee.data[section])
      : currentEmployee.data[section][field];
    
    setEditedData(prev => ({ ...prev, [key]: value }));
  };

  const saveEdit = (section, field) => {
    const key = `${section}.${field}`;
    const newValue = editedData[key];
    
    if (!newValue || newValue.toString().trim() === '') {
      showToast('Field cannot be empty', 'warning');
      return;
    }
    
    setEmployees(prev => {
      const updated = [...prev];
      const employee = { ...updated[currentEmployeeIndex] };
      
      if (field.includes('.')) {
        const keys = field.split('.');
        let current = employee.data[section];
        for (let i = 0; i < keys.length - 1; i++) {
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = newValue;
      } else {
        employee.data[section][field] = newValue;
      }
      
      updated[currentEmployeeIndex] = employee;
      return updated;
    });
    
    setIsEditing(prev => ({ ...prev, [key]: false }));
    showToast('Field updated successfully', 'success');
  };

  const cancelEdit = (section, field) => {
    const key = `${section}.${field}`;
    setIsEditing(prev => ({ ...prev, [key]: false }));
    setEditedData(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };
  const { createEmployee } = useEmployeeStream(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/employees/stream`);

  const handleAccept = async () => {
    try {
      setIsSaving(true);
      showToast('Saving employee data...', 'info');
      
      const response = await apiClient.post(`/v1/employees/one-upload`, {...currentEmployee.data, accept: true});
      console.log('Employee onboarded successfully! -->', response);
      
      showToast('Employee onboarded successfully!', 'success');
      //   log thi payload data
      console.log('Employee onboarded successfully!', currentEmployee);
      createEmployee({...currentEmployee.data, isActive: true});
      
      setEmployees(prev => prev.filter((_, index) => index !== currentEmployeeIndex));
      
      if (currentEmployeeIndex >= employees.length - 1) {
        setCurrentEmployeeIndex(Math.max(0, currentEmployeeIndex - 1));
      }
    } catch (error) {
      showToast(error.response.data.detail, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeny = async () => {
    if (window.confirm('Are you sure you want to deny this onboarding? This action cannot be undone.')) {
      showToast('Employee onboarding denied', 'warning');
      
      setEmployees(prev => prev.filter((_, index) => index !== currentEmployeeIndex));
      
      if (currentEmployeeIndex >= employees.length - 1) {
        setCurrentEmployeeIndex(Math.max(0, currentEmployeeIndex - 1));
      }
    }
  };

  // Utility functions - Fixed to work with object structure
  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <Loader2 className="animate-spin h-4 w-4 text-blue-500" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'processing':
        return 'bg-blue-50 border-blue-200';
      case 'completed':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const renderEditableField = (section, field, label, value, type = 'text') => {
    const key = `${section}.${field}`;
    const editing = isEditing[key];
    
    if (Array.isArray(value)) {
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
          <div className="flex flex-wrap gap-2">
            {value.length > 0 ? value.map((item, index) => (
              <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                {item}
              </span>
            )) : <span className="text-gray-500 italic">No data available</span>}
          </div>
        </div>
      );
    }

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <input
                type={type}
                value={editedData[key] || ''}
                onChange={(e) => setEditedData(prev => ({ ...prev, [key]: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <button
                onClick={() => saveEdit(section, field)}
                className="p-2 text-green-600 hover:bg-green-50 rounded"
              >
                <Save size={16} />
              </button>
              <button
                onClick={() => cancelEdit(section, field)}
                className="p-2 text-red-600 hover:bg-red-50 rounded"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <>
              <span className="flex-1 px-3 py-2 bg-gray-50 rounded border text-gray-900">
                {value || 'Not specified'}
              </span>
              <button
                onClick={() => startEditing(section, field)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
              >
                <Edit2 size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  // Toast component
  const ToastContainer = () => (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`flex items-center p-4 rounded-lg shadow-lg max-w-sm animate-slide-in ${
            toast.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
            toast.type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
            toast.type === 'warning' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
            'bg-blue-100 text-blue-800 border border-blue-200'
          }`}
        >
          <div className="flex items-center">
            {toast.type === 'success' && <CheckCircle size={20} className="mr-2" />}
            {toast.type === 'error' && <XCircle size={20} className="mr-2" />}
            {toast.type === 'warning' && <AlertCircle size={20} className="mr-2" />}
            {toast.type === 'info' && <Info size={20} className="mr-2" />}
            <span className="flex-1">{toast.message}</span>
          </div>
          <button
            onClick={() => removeToast(toast.id)}
            className="ml-4 text-current opacity-70 hover:opacity-100"
          >
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
  );

  // Navigation component - Fixed to show correct count
  const Navigation = () => {
    const tasksCount = Object.keys(tasksInProgress).length;
    
    return (
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8 overflow-auto">
          <button
            onClick={() => setCurrentView('new')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'new'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Create New Onboarding Entry
          </button>
          {user.is_superuser&&<>
          <button
            onClick={() => setCurrentView('upload')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'upload'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Upload Documents
            {tasksCount > 0 && (
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                {tasksCount}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setCurrentView('review');
              fetchProcessedEmployees();
            }}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'review'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Review Employees
            {employees.length > 0 && (
              <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                {employees.length}
              </span>
            )}
          </button>
          {/* Employee Updates Request */}
          <button
            onClick={() => setCurrentView('requests')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'requests'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Employee Updates Request
          </button>
          <button
            onClick={() => setCurrentView('forms')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'forms'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Forms
          </button>
          <button
            onClick={() => setCurrentView('users-management')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'users-management'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Users Management
          </button>
          {/* <button
            onClick={() => setCurrentView('PDF-Upload')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              currentView === 'PDF-Upload'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Merge PDF
          </button> */}
          </>}
        </nav>
      </div>
    );
  };

  const renderYearlyCompensation = (yearlyData) => {
    if (!yearlyData) return null;
    
    return (
      <div className="grid grid-cols-2 gap-4">
        {Object.entries(yearlyData).map(([year, data]) => (
          <div key={year} className="border rounded p-3 bg-gray-50">
            <h5 className="font-medium text-gray-700 mb-2">{year}</h5>
            <div className="space-y-1 text-sm">
              <div>CTC: {data.ctc || 'Not specified'}</div>
              <div>Basic: {data.basic || 'Not specified'}</div>
              <div>Gross: {data.gross || 'Not specified'}</div>
              <div>Incentive: {data.incentive || 'Not specified'}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderNomineeInformation = (nominees) => {
    if (!nominees || nominees.length === 0) return <span className="text-gray-500 italic">No nominees specified</span>;
    
    return (
      <div className="space-y-3">
        {nominees.map((nominee, index) => (
          <div key={index} className="border rounded p-3 bg-gray-50">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><strong>Name:</strong> {nominee.nominee_name || 'Not specified'}</div>
              <div><strong>Relationship:</strong> {nominee.relationship || 'Not specified'}</div>
              <div><strong>Age:</strong> {nominee.nominee_age || 'Not specified'}</div>
              <div><strong>DOB:</strong> {nominee.nominee_dob || 'Not specified'}</div>
              <div><strong>Share %:</strong> {nominee.share_percentage || 'Not specified'}</div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderEducationSection = (education) => {
    if (!education) return null;
    
    const educationLevels = ['tenth', 'twelfth', 'graduation', 'post_graduation', 'professional'];
    
    return (
      <div className="space-y-3">
        {educationLevels.map(level => {
          const eduData = education[level];
          if (!eduData) return null;
          
          return (
            <div key={level} className="border rounded p-3 bg-gray-50">
              <h5 className="font-medium text-gray-700 mb-2 capitalize">{level.replace('_', ' ')}</h5>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><strong>Completed:</strong> {eduData.completed || 'Not specified'}</div>
                <div><strong>Source:</strong> {eduData.education_source || 'Not specified'}</div>
                {eduData.degree && <div><strong>Degree:</strong> {eduData.degree}</div>}
              </div>
            </div>
          );
        })}
        
        {education.other && education.other.length > 0 && (
          <div className="border rounded p-3 bg-gray-50">
            <h5 className="font-medium text-gray-700 mb-2">Other Qualifications</h5>
            <div className="text-sm">
              {education.other.map((qual, index) => (
                <div key={index}>{qual.degree}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <ToastContainer />
      
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-600 to-green-300 bg-clip-text text-transparent">Employee Onboarding System</h1>
        <p className="text-gray-600">Upload documents and review processed employee data</p>
      </div>

      <Navigation />

      {currentView==="new"&&(
        <HROnboardingPanel />
      )}

      {currentView === 'upload' && (
        // Upload View
        <div>
          {/* Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center mb-6 bg-gray-50 relative">
              {/* Employee ID Input */}
              <div className="mb-6 w-full max-w-md mx-auto text-left">
                <label htmlFor="employeeId" className="block text-sm font-semibold text-gray-700 mb-2">
                  Enter Employee ID
                </label>
                <input
                  id="employeeId"
                  name="employeeId"
                  onChange={(e)=>setEmployeeId(e.target.value)}
                  type="text"
                  placeholder="E.g., 12345"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-500 transition duration-150"
                />
              </div>

              {/* PDF Upload Section */}
              <PDFMergeUploader onUpload={handleFileUpload} />

            </div>



          {/* Tasks in Progress - Fixed to handle object structure */}
          {Object.keys(tasksInProgress).length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Processing Status ({Object.keys(tasksInProgress).length} files)
              </h3>
              <div className="space-y-3">
                {Object.entries(tasksInProgress).map(([taskId, task]) => {
                  const metadata = taskMetadata[taskId] || {};
                  return (
                    <div key={taskId} className={`border rounded-lg p-4 ${getStatusColor(task.status)}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                          <FileText className="h-5 w-5 text-red-500 mr-3" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {metadata.filename || `Task ${taskId.substring(0, 8)}...`}
                              </span>
                              {getStatusIcon(task.status)}
                              <span className="text-xs text-gray-600 capitalize">{task.status}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {metadata.file_size && (
                                <>Size: {(metadata.file_size / 1024 / 1024).toFixed(2)} MB | </>
                              )}
                              {metadata.uploaded_at && (
                                <>Uploaded: {new Date(metadata.uploaded_at).toLocaleTimeString()}</>
                              )}
                            </div>
                            {task.status === 'processing' && (
                              <div className="mt-2">
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                    style={{ width: `${task.progress || 10}%` }}
                                  ></div>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {task.progress ? `${task.progress}%` : 'Processing...'}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {task.status === 'completed' && (
                          <button
                            onClick={() => {
                              setCurrentView('review');
                              fetchProcessedEmployees();
                            }}
                            className="ml-4 bg-green-100 text-green-700 px-3 py-1 rounded text-sm hover:bg-green-200 flex items-center"
                          >
                            <Eye size={14} className="mr-1" />
                            Review
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Information Panel */}
          <div className="mt-6 p-4 bg-blue-50 rounded-md">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Upload Information:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Files are processed in the background - you can upload multiple files</li>
                  <li>Maximum file size is 20MB per PDF document</li>
                  <li>You'll receive notifications when processing is complete</li>
                  <li>Use the "Review Employees" tab to examine processed employee data</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) }
       {currentView === 'review' && (
        // Review View - Updated for new data format
        <div>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin h-8 w-8 text-blue-600 mr-3" />
              <span className="text-gray-600">Loading processed employees...</span>
            </div>
          ) : employees.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No Employees to Review</h2>
              <p className="text-gray-600 mb-6">There are currently no processed employee documents to review.</p>
              <div className="space-x-4">
                <button
                  onClick={fetchProcessedEmployees}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center mx-auto"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Refresh
                </button>
              </div>
            </div>
          ) : (
            <div>
              {/* Employee Navigation */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Employee {currentEmployeeIndex + 1} of {employees.length}
                  </h2>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentEmployeeIndex(Math.max(0, currentEmployeeIndex - 1))}
                      disabled={currentEmployeeIndex === 0}
                      className="px-3 py-1 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentEmployeeIndex(Math.min(employees.length - 1, currentEmployeeIndex + 1))}
                      disabled={currentEmployeeIndex === employees.length - 1}
                      className="px-3 py-1 bg-white border rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600">
                  <p><strong>Employee ID:</strong> {currentEmployee.data.eid || currentEmployee.id}</p>
                  <p><strong>Name:</strong> {currentEmployee.data.personal_information?.employee_name || currentEmployee.filename || 'Not specified'}</p>
                  <p><strong>Processed:</strong> {new Date(currentEmployee.processed_at).toLocaleString()}</p>
                  <p><strong>Status:</strong> 
                    <span className={`ml-2 px-2 py-1 rounded text-xs ${
                      currentEmployee.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                      currentEmployee.status === 'approved' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {currentEmployee.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </p>
                </div>
              </div>

              {/* Employee Data Form - Updated for new data format */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div className="space-y-6">
                  {/* Personal Information */}
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                    {currentEmployee.data.personal_information && (
                      <>
                        {renderEditableField('personal_information', 'employee_name', 'Employee Name', currentEmployee.data.personal_information.employee_name)}
                        {renderEditableField('personal_information', 'father_name', 'Father Name', currentEmployee.data.personal_information.father_name)}
                        {renderEditableField('personal_information', 'mother_name', 'Mother Name', currentEmployee.data.personal_information.mother_name)}
                        {renderEditableField('personal_information', 'date_of_birth', 'Date of Birth', currentEmployee.data.personal_information.date_of_birth, 'date')}
                        {renderEditableField('personal_information', 'place_of_birth', 'Place of Birth', currentEmployee.data.personal_information.place_of_birth)}
                        {renderEditableField('personal_information', 'gender', 'Gender', currentEmployee.data.personal_information.gender)}
                        {renderEditableField('personal_information', 'nationality', 'Nationality', currentEmployee.data.personal_information.nationality)}
                        {renderEditableField('personal_information', 'religion', 'Religion', currentEmployee.data.personal_information.religion)}
                        {renderEditableField('personal_information', 'marital_status', 'Marital Status', currentEmployee.data.personal_information.marital_status)}
                        {renderEditableField('personal_information', 'blood_group', 'Blood Group', currentEmployee.data.personal_information.blood_group)}
                        {renderEditableField('personal_information', 'languages_known', 'Languages Known', currentEmployee.data.personal_information.languages_known)}
                      </>
                    )}
                  </div>

                  {/* Address Information */}
                  {currentEmployee.data.address_information && (
                    <div className="bg-white border rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Address Information</h3>
                      
                      {/* Permanent Address */}
                      <h4 className="text-md font-medium text-gray-800 mb-3">Permanent Address</h4>
                      {currentEmployee.data.address_information.permanent_address && (
                        <>
                          {renderEditableField('address_information', 'permanent_address.house', 'House/Flat No.', currentEmployee.data.address_information.permanent_address.house)}
                          {renderEditableField('address_information', 'permanent_address.street', 'Street', currentEmployee.data.address_information.permanent_address.street)}
                          {renderEditableField('address_information', 'permanent_address.city_or_village', 'City/Village', currentEmployee.data.address_information.permanent_address.city_or_village)}
                          {renderEditableField('address_information', 'permanent_address.district', 'District', currentEmployee.data.address_information.permanent_address.district)}
                          {renderEditableField('address_information', 'permanent_address.state', 'State', currentEmployee.data.address_information.permanent_address.state)}
                          {renderEditableField('address_information', 'permanent_address.pincode', 'Pincode', currentEmployee.data.address_information.permanent_address.pincode)}
                        </>
                      )}
                    </div>
                  )}

                  {/* Contact Information */}
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                    {currentEmployee.data.contact_information && (
                      <>
                        {renderEditableField('contact_information', 'phone_number', 'Phone Number', currentEmployee.data.contact_information.phone_number)}
                        {renderEditableField('contact_information', 'email', 'Email', currentEmployee.data.contact_information.email, 'email')}
                        {currentEmployee.data.contact_information.emergency_contact && (
                          <>
                            {renderEditableField('contact_information', 'emergency_contact.name', 'Emergency Contact Name', currentEmployee.data.contact_information.emergency_contact.name)}
                            {renderEditableField('contact_information', 'emergency_contact.mobile', 'Emergency Contact Mobile', currentEmployee.data.contact_information.emergency_contact.mobile)}
                            {renderEditableField('contact_information', 'emergency_contact.email', 'Emergency Contact Email', currentEmployee.data.contact_information.emergency_contact.email, 'email')}
                          </>
                        )}
                      </>
                    )}
                  </div>

                  {/* Identification Documents */}
                  {currentEmployee.data.identification_documents && (
                    <div className="bg-white border rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Identification Documents</h3>
                      {renderEditableField('identification_documents', 'aadhaar_number', 'Aadhaar Number', currentEmployee.data.identification_documents.aadhaar_number)}
                      {renderEditableField('identification_documents', 'pan_number', 'PAN Number', currentEmployee.data.identification_documents.pan_number)}
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  {/* Employment Details */}
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Employment Details</h3>
                    {currentEmployee.data.employment_details && (
                      <>
                        {renderEditableField('employment_details', 'designation', 'Designation', currentEmployee.data.employment_details.designation)}
                        {renderEditableField('employment_details', 'grade', 'Grade', currentEmployee.data.employment_details.grade)}
                        {renderEditableField('employment_details', 'eid', 'Employee ID', currentEmployee.data.employment_details.eid)}
                        {renderEditableField('employment_details', 'uan_number', 'UAN Number', currentEmployee.data.employment_details.uan_number)}
                        {renderEditableField('employment_details', 'date_of_joining', 'Date of Joining', currentEmployee.data.employment_details.date_of_joining, 'date')}
                        {renderEditableField('employment_details', 'date_of_appointment', 'Date of Appointment', currentEmployee.data.employment_details.date_of_appointment, 'date')}
                        {renderEditableField('employment_details', 'location', 'Location', currentEmployee.data.employment_details.location)}
                        {renderEditableField('employment_details', 'division', 'Division', currentEmployee.data.employment_details.division)}
                        {renderEditableField('employment_details', 'department', 'Department', currentEmployee.data.employment_details.department)}
                        {renderEditableField('employment_details', 'reporting_manager', 'Reporting Manager', currentEmployee.data.employment_details.reporting_manager)}
                        {renderEditableField('employment_details', 'source_of_candidate', 'Source of Candidate', currentEmployee.data.employment_details.source_of_candidate)}
                        {renderEditableField('employment_details', 'last_organisation', 'Last Organisation', currentEmployee.data.employment_details.last_organisation)}
                        {renderEditableField('employment_details', 'total_experience', 'Total Experience (Years)', currentEmployee.data.employment_details.total_experience, 'number')}
                        {renderEditableField('employment_details', 'official_email', 'Official Email', currentEmployee.data.employment_details.official_email, 'email')}
                      </>
                    )}
                  </div>

                  {/* Bank Details */}
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h3>
                    {currentEmployee.data.bank_details && (
                      <>
                        {renderEditableField('bank_details', 'bank_name', 'Bank Name', currentEmployee.data.bank_details.bank_name)}
                        {renderEditableField('bank_details', 'account_number', 'Account Number', currentEmployee.data.bank_details.account_number)}
                        {renderEditableField('bank_details', 'ifsc_code', 'IFSC Code', currentEmployee.data.bank_details.ifsc_code)}
                      </>
                    )}
                  </div>

                  {/* Salary Information */}
                  {currentEmployee.data.salary_and_compensation && (
                    <div className="bg-white border rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Salary Information</h3>
                      
                      {/* At Joining */}
                      {currentEmployee.data.salary_and_compensation.at_joining && (
                        <div className="mb-6">
                          <h4 className="text-md font-medium text-gray-800 mb-3">At Joining</h4>
                          {renderEditableField('salary_and_compensation', 'at_joining.ctc', 'CTC at Joining', currentEmployee.data.salary_and_compensation.at_joining.ctc, 'number')}
                          {renderEditableField('salary_and_compensation', 'at_joining.basic', 'Basic at Joining', currentEmployee.data.salary_and_compensation.at_joining.basic, 'number')}
                          {renderEditableField('salary_and_compensation', 'at_joining.gross', 'Gross at Joining', currentEmployee.data.salary_and_compensation.at_joining.gross, 'number')}
                          {renderEditableField('salary_and_compensation', 'at_joining.incentive', 'Incentive at Joining', currentEmployee.data.salary_and_compensation.at_joining.incentive, 'number')}
                        </div>
                      )}
                      
                      {/* Yearly Compensation */}
                      {currentEmployee.data.salary_and_compensation.yearly_compensation && (
                        <div>
                          <h4 className="text-md font-medium text-gray-800 mb-3">Yearly Compensation</h4>
                          {renderYearlyCompensation(currentEmployee.data.salary_and_compensation.yearly_compensation)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Nominee Information */}
                  {currentEmployee.data.nominee_information && (
                    <div className="bg-white border rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Nominee Information</h3>
                      {renderNomineeInformation(currentEmployee.data.nominee_information)}
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Sections - Full Width */}
              <div className="mt-6 space-y-6">
                {/* Education */}
                {currentEmployee.data.education && (
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Education</h3>
                    {renderEducationSection(currentEmployee.data.education)}
                  </div>
                )}

                {/* Declarations and Compliance */}
                {currentEmployee.data.declarations_and_compliance && (
                  <div className="bg-white border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Declarations and Compliance</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {renderEditableField('declarations_and_compliance', 'physical_disabilities', 'Physical Disabilities', currentEmployee.data.declarations_and_compliance.physical_disabilities)}
                      {renderEditableField('declarations_and_compliance', 'major_illness', 'Major Illness', currentEmployee.data.declarations_and_compliance.major_illness)}
                      {renderEditableField('declarations_and_compliance', 'court_cases', 'Court Cases', currentEmployee.data.declarations_and_compliance.court_cases)}
                      {renderEditableField('declarations_and_compliance', 'police_custody', 'Police Custody', currentEmployee.data.declarations_and_compliance.police_custody)}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-8 flex justify-center space-x-4">
                <button
                  onClick={handleDeny}
                  disabled={isSaving}
                  className="flex items-center px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed transition-colors"
                >
                  <X size={16} className="mr-2" />
                  Deny Onboarding
                </button>
                <button
                  onClick={handleAccept}
                  disabled={isSaving}
                  className="flex items-center px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check size={16} className="mr-2" />
                      Accept & Complete Onboarding
                    </>
                  )}
                </button>
              </div>

              {/* Information Panel */}
              <div className="mt-6 p-4 bg-blue-50 rounded-md">
                <div className="flex items-start">
                  <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Review Instructions:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>Click the edit icon next to any field to modify incorrect information</li>
                      <li>Use Previous/Next buttons to navigate between employees</li>
                      <li>All changes are saved automatically when you click the save button</li>
                      <li>Accept to complete onboarding or deny to reject the application</li>
                      <li>Approved employees will be added to the system and removed from this queue</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="mt-4 flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={fetchProcessedEmployees}
                    className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
                  >
                    <RefreshCw size={16} className="mr-2" />
                    Refresh List
                  </button>
                  
                  <span className="text-sm text-gray-500">
                    {employees.length} employee{employees.length !== 1 ? 's' : ''} pending review
                  </span>
                </div>
                
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <span>Auto-refresh in progress</span>
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {currentView === "forms" && (
        <AllEforms />
      )}
      {currentView === "requests" && (
        <EmployeeUpdateRequest />
      )}

      {currentView === "users-management" && (
        <UserManagement />
      )}
      {currentView === "PDF-Upload" && (
        <PDFMergeUploader />
      )}
      
    </div>
  );
};

export default MainOnboardingSystem;